import { CloseIcon, SearchIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  SimpleGrid,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react'
import { debounce } from 'lodash'
import React, { useCallback, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import DreamMeaningsSk from '../data/dreamMeaningsSk.grouped.json'
import DreamMeaningsCs from '../data/dreamMeaningsCs.grouped.json'
import { language, Meaning, pageTitle } from '../global'
import { useTranslation } from 'react-i18next'

export const DreamSymbolCard: React.FC<{ symbol: Meaning }> = ({ symbol }) => {
  const { t } = useTranslation()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('purple.100', 'purple.700')
  const textColor = useColorModeValue('gray.600', 'gray.300')
  const headingColor = useColorModeValue('purple.600', 'purple.300')

  return (
    <Flex
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      p={4}
      bg={bgColor}
      borderColor={borderColor}
    >
      <VStack
        align="stretch"
        spacing={2}
        justifyContent="space-between"
        flex={1}
      >
        <Box>
          <Heading size="md" color={headingColor}>
            {symbol.symbol}
          </Heading>
          <Text fontSize="sm" color={textColor}>
            {symbol.detail}
          </Text>
        </Box>
        <Box>
          <Text fontSize="sm" color={textColor} fontWeight="medium">
            <Text as="span" fontWeight="bold">
              {t('common.meaning')}:
            </Text>{' '}
            {symbol.meaning}
          </Text>
        </Box>
      </VStack>
    </Flex>
  )
}

// Define the type for our dream symbols data structure
interface DreamSymbolsData {
  [key: string]: Meaning[]
}

const normalizeString = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

const DreamBook: React.FC = () => {
  const { t } = useTranslation()
  const DreamMeanings = language === 'sk' ? DreamMeaningsSk : DreamMeaningsCs
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('')
  const [activeLetter, setActiveLetter] = useState<string | null>('a')

  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const textColor = useColorModeValue('gray.600', 'gray.300')
  const headingColor = useColorModeValue('purple.600', 'purple.300')
  const resultBgColor = useColorModeValue('white', 'gray.800')

  const allSymbols = useMemo(() => {
    return Object.values(DreamMeanings as DreamSymbolsData)
      .flat()
      .map((symbol) => ({
        ...symbol,
        normalizedSymbol: normalizeString(symbol.symbol.toLowerCase()),
      }))
  }, [])

  // Debounce the search term update
  const debouncedSetSearchTerm = useCallback(
    debounce((value: string) => setDebouncedSearchTerm(value), 300),
    [debounce],
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    debouncedSetSearchTerm(value)
  }

  const clearSearch = () => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
  }

  const filteredSymbols: Meaning[] = useMemo(() => {
    if (!debouncedSearchTerm && !activeLetter) return []

    if (!debouncedSearchTerm && activeLetter) {
      //@ts-ignore
      return DreamMeanings[activeLetter] || []
    }

    const normalizedSearchTerm = normalizeString(
      debouncedSearchTerm.toLowerCase(),
    )
    return allSymbols.filter((symbol) =>
      symbol.normalizedSymbol.includes(normalizedSearchTerm),
    )
  }, [debouncedSearchTerm, activeLetter, allSymbols])

  const handleLetterClick = (letter: string) => {
    setActiveLetter(letter)
    setSearchTerm('')
    setDebouncedSearchTerm('')
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle(t('dreamBook.title'))}</title>
        <meta name="description" content={t('dreamBook.description')} />
      </Helmet>
      <Box bg={bgColor} minH="100vh">
        <Container maxW="container.xl" py={10}>
          <VStack spacing={10} align="stretch">
            <VStack spacing={4}>
              <Heading
                as="h1"
                size="2xl"
                textAlign="center"
                color={headingColor}
              >
                {t('dreamBook.title')}
              </Heading>
              <Text
                fontSize="xl"
                textAlign="center"
                color={textColor}
                maxW="2xl"
                mx="auto"
              >
                {t('dreamBook.description')}
              </Text>
            </VStack>
            <Box position="relative">
              <InputGroup>
                <Input
                  placeholder={t('dreamBook.search.placeholder')}
                  size="lg"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  pr="4.5rem"
                  borderColor={headingColor}
                  backgroundColor={'white'}
                />
                <InputRightElement
                  width="4.5rem"
                  top="50%"
                  transform="translateY(-50%)"
                >
                  {searchTerm ? (
                    <IconButton
                      aria-label="Clear search"
                      icon={<CloseIcon />}
                      size="sm"
                      onClick={clearSearch}
                    />
                  ) : (
                    <SearchIcon color={textColor} />
                  )}
                </InputRightElement>
              </InputGroup>
            </Box>

            {!debouncedSearchTerm && (
              <HStack spacing={2} wrap="wrap" justify="center">
                {Object.keys(DreamMeanings).map((letter) => (
                  <Button
                    key={letter}
                    size="sm"
                    variant={activeLetter === letter ? 'solid' : 'outline'}
                    onClick={() => handleLetterClick(letter)}
                    colorScheme="purple"
                  >
                    {letter.toUpperCase()}
                  </Button>
                ))}
              </HStack>
            )}
          </VStack>
        </Container>

        <Box bg={resultBgColor}>
          <Container maxW="container.xl" py={10}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {filteredSymbols.map((symbol, index) => (
                <DreamSymbolCard key={index} symbol={symbol} />
              ))}
            </SimpleGrid>
            {filteredSymbols.length === 0 && (
              <Text textAlign="center" fontSize="lg" color={textColor}>
                {debouncedSearchTerm
                  ? t('dreamBook.search.noResults')
                  : t('dreamBook.search.selectLetter')}
              </Text>
            )}
          </Container>
        </Box>

        <Container maxW="container.xl" py={10}>
          <VStack spacing={10}>
            {!debouncedSearchTerm && (
              <HStack spacing={2} wrap="wrap" justify="center">
                {Object.keys(DreamMeanings).map((letter) => (
                  <Button
                    key={letter}
                    size="sm"
                    variant={activeLetter === letter ? 'solid' : 'outline'}
                    onClick={() => handleLetterClick(letter)}
                    colorScheme="purple"
                  >
                    {letter.toUpperCase()}
                  </Button>
                ))}
              </HStack>
            )}
            <Box>
              <Heading as="h2" size="lg" mb={4} color={headingColor}>
                {t('dreamBook.why.title')}
              </Heading>
              <Text color={textColor} mb={4}>
                {t('dreamBook.why.text1')}
              </Text>
              <Text color={textColor} mb={4}>
                {t('dreamBook.why.text2')}
              </Text>
              <Text color={textColor}>{t('dreamBook.why.text3')}</Text>
            </Box>
          </VStack>
        </Container>
      </Box>
    </>
  )
}

export default DreamBook
